// Notifications store — M8-S3.
// SSE ile backend'e subscribe olur; unreadCount + son N bildirimi tutar.
// AppShell'deki zil ikonu bu store'u okur.

import { create } from "zustand";
import type { Notification, NotificationListResponse } from "@mahir/shared";
import { api } from "../api/client";

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  total: number;
  loading: boolean;
  sseConnected: boolean;
  error: string | null;
  // actions
  load: (opts?: { unreadOnly?: boolean; pageSize?: number }) => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  handleIncoming: (n: Notification) => void;
  connectSSE: () => void;
  disconnectSSE: () => void;
  setSSEConnected: (v: boolean) => void;
}

let eventSource: EventSource | null = null;

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  total: 0,
  loading: false,
  sseConnected: false,
  error: null,

  load: async (opts) => {
    set({ loading: true, error: null });
    try {
      const qs = new URLSearchParams();
      qs.set("pageSize", String(opts?.pageSize ?? 20));
      if (opts?.unreadOnly) qs.set("unreadOnly", "true");
      const res = await api<NotificationListResponse>(`/notifications?${qs.toString()}`);
      set({
        notifications: res.notifications,
        unreadCount: res.unreadCount,
        total: res.total,
        loading: false,
      });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Bildirim yüklenemedi", loading: false });
    }
  },

  markRead: async (id: string) => {
    try {
      const res = await api<{ unreadCount: number }>(`/notifications/${id}/read`, {
        method: "PATCH",
      });
      set((state) => ({
        unreadCount: res.unreadCount,
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, readAt: new Date().toISOString() } : n,
        ),
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "İşlem başarısız" });
    }
  },

  markAllRead: async () => {
    try {
      await api<{ markedAsRead: number }>("/notifications/read-all", { method: "POST" });
      set((state) => ({
        unreadCount: 0,
        notifications: state.notifications.map((n) =>
          n.readAt ? n : { ...n, readAt: new Date().toISOString() },
        ),
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "İşlem başarısız" });
    }
  },

  handleIncoming: (n) => {
    set((state) => {
      // duplicate guard
      if (state.notifications.some((x) => x.id === n.id)) return state;
      return {
        notifications: [n, ...state.notifications].slice(0, 50),
        unreadCount: n.readAt ? state.unreadCount : state.unreadCount + 1,
        total: state.total + 1,
      };
    });
  },

  connectSSE: () => {
    if (eventSource) return;
    // EventSource cookie-auth destekler (withCredentials). Vite dev'te proxy /api → backend.
    const url = `/api/v1/notifications/stream`;
    const es = new EventSource(url, { withCredentials: true });
    eventSource = es;

    es.addEventListener("open", () => {
      get().setSSEConnected(true);
    });

    es.addEventListener("notification", (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent<string>).data) as Notification;
        get().handleIncoming(data);
      } catch {
        // ignore
      }
    });

    es.addEventListener("error", () => {
      get().setSSEConnected(false);
      // Otomatik reconnect — EventSource default
    });
  },

  disconnectSSE: () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
      set({ sseConnected: false });
    }
  },

  setSSEConnected: (v) => set({ sseConnected: v }),
}));
